import { colors, spacing } from "@indeed/ifl-tokens/react-native";
import { RNApplyButtonTypeV3 } from "@indeed/signals-schema/com/indeed/signals/schema/viewjob/RNApplyButtonTypeV3";
import React, {
  type ReactElement,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Platform, StyleSheet, useWindowDimensions } from "react-native";

import type { BoxRef } from "../../../../../../mads";
import {
  Button,
  type ButtonInteractionStyles,
  type ButtonSize,
  type ButtonVariant,
} from "../../../../../../mads/Button";
import { useI18n } from "../../../../../../shared/I18n";
import { useLogErrorOnce } from "../../../../../../shared/logging/useLogErrorOnce";
import { useTrackVisibility } from "../../../../../../shared/logging/visibility-tracker";
import { logTapButton } from "../../../../../../shared/native-modules/tare/logTapButton";
import { useTareData } from "../../../../../../shared/native-modules/tare/TareDataContext";
import eventEmitter, {
  ApplyType,
  ApplyStatus,
  NativeApplyResult,
} from "../../../../../../shared/util/eventEmitter";
import { useIsBottomSheet } from "../../../../../../shared/util/ScreenType";
import { useHandleStartIndeedApply } from "../../../../../native-module/handleStartIndeedApply";
import { updateApplyStartCookie } from "../../../../../native-module/updateApplyStartCookie";
import { getEnvironmentConfig } from "../../../../config/urls";
import { useDwellTiming } from "../../../../context/DwellTiming";
import {
  useJobClickTrackingAttributes,
  useViewJobDataContext,
  useViewjobOneGraphModel,
} from "../../../../context/ViewJobDataContext";
import { useRnViewJobDisplay } from "../../../../context/ViewJobDisplayContext";
import { useIsDesktop } from "../../../../context/ViewJobScreenDisplay";
import { useEmployerOutreachInfo } from "../../../../hooks/useEmployerOutreachInfo";
import { useEmployerOutreachSource } from "../../../../hooks/useEmployerOutreachSource";
import type { IndeedApplyButtonContainer } from "../../../../viewjobOneGraphModel";
import { handleEmployerOutreachApply } from "../EmployerOutreach/utils/handleEmployerOutreachApply";
import { logApplyButtonShowEvent, type ButtonDimensions } from "../utils/applyButtonLogging";
import {
  resolveRawClickSurface,
  triggerJobClickTracking,
  triggerRawClickLogging,
} from "../ViewJobButtonLink/jobClickTracking";

export type JobModel = {
  key: string;
  title?: string;
};

export type ViewJobDetailsModel = {
  mobvjtk?: string;
  jobCountry?: string;
};

export interface IndeedApplyButtonProps {
  indeedApplyButtonContainer: IndeedApplyButtonContainer;
  jobModel: JobModel;
  viewJobDetailsModel: ViewJobDetailsModel;
  domain: string;
  variant?: ButtonVariant;
  isSecondaryApplyButton?: boolean;
  buttonText?: string;
  forceEnabled?: boolean;
  disabled?: boolean;

  iconBefore?: ReactElement;
  iconAfter?: ReactElement;
  onFirstImpression?: () => void;
  onClick?: () => void;
  skipNativeApplyBridge?: boolean;
  hasLoggedImpression: RefObject<boolean>;
  hasLoggedSeen: boolean;
  setHasLoggedSeen: (value: boolean) => void;
  buttonSize?: ButtonSize | undefined;
  isApplyCtaTestActive?: boolean;
}

const brandedDesktopWebApplyInteractionStyles = {
  hover: {
    backgroundColor: colors.primary900,
    backgroundImage: "none",
    borderColor: colors.primary900,
  },
  active: {
    backgroundColor: colors.primary1000,
    backgroundImage: "none",
    borderColor: colors.primary1000,
  },
} satisfies ButtonInteractionStyles;

export function IndeedApplyButton({
  indeedApplyButtonContainer,
  jobModel,
  viewJobDetailsModel,
  domain,
  variant,
  isSecondaryApplyButton,
  forceEnabled,
  disabled: disabledProp = false,
  buttonText,
  buttonSize = "lg",
  iconBefore,
  iconAfter,
  onFirstImpression,
  onClick,
  skipNativeApplyBridge,
  hasLoggedImpression,
  hasLoggedSeen,
  setHasLoggedSeen,
  isApplyCtaTestActive = false,
}: IndeedApplyButtonProps): React.JSX.Element {
  const [hasLoggedVisibility, setHasLoggedVisibility] = useState(false);
  const buttonRef = useRef<BoxRef>(null);
  const i18n = useI18n();
  const logErrorOnce = useLogErrorOnce();
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();

  const {
    contentHtml,
    indeedApplyUrl,
    disabled: modelDisabled,
    tmnUrl,
  } = indeedApplyButtonContainer;
  const { key: jobKey } = jobModel;
  const { mobvjtk: mobtk } = viewJobDetailsModel;

  const ogModel = useViewjobOneGraphModel();
  const { accountKey, ctk, employerOutreachEngagement } = ogModel ?? {};
  const invite = employerOutreachEngagement?.engagements?.[0];

  const rnViewJobDisplay = useRnViewJobDisplay();
  const isBottomSheet = useIsBottomSheet();
  const isDesktopEmbedded = rnViewJobDisplay === "DESKTOP_EMBEDDED";
  const isDesktop = useIsDesktop();
  const isBrandedWebApply = isApplyCtaTestActive;
  const { from, initialData, proctorGroups } = useViewJobDataContext();
  const tareData = useTareData();
  const buildEnvironment = tareData?.appModel?.buildEnvironment;
  const environmentConfig = getEnvironmentConfig(buildEnvironment);
  const rezcowBaseUrl = environmentConfig.rezcowBaseUrl;
  const resumeBaseUrl = environmentConfig.resumeBaseUrl;
  const isLoggedIn = tareData?.accountStatus?.isLoggedIn ?? false;
  const accountId = tareData?.accountStatus?.accountId;

  const jobClickTrackingAttributes = useJobClickTrackingAttributes();
  const hasFiredJobClickTracking = useRef(false);

  const { isFromEmployerOutreach, outreachSource, from: entryPoint } = useEmployerOutreachSource();
  const { outreachInfo } = useEmployerOutreachInfo(jobKey) ?? {};

  const handleStartIndeedApply = useHandleStartIndeedApply();
  const { markApplyStart } = useDwellTiming();

  const onClickHandler = useCallback(async () => {
    if (skipNativeApplyBridge) {
      onClick?.();
      return;
    }

    if (tmnUrl) {
      void logTapButton("rn-view-job-tmn-apply", "tmn-apply-button");
    }

    // Auto-open (desktop two-pane) parity: when the viewjob was auto-opened the user
    // never clicked the job card, so the job click is counted here on apply. Mirrors
    // legacy web ReactIAButtonWithJsLogging/onApplyButtonClick. Web-only and fires at
    // most once; manual-click jobs leave jobClickTrackingAttributes undefined.
    // The matching job-card rawclick is emitted below with the same one-time gate.
    const shouldFireAutoOpenJobClick =
      Platform.OS === "web" && !!jobClickTrackingAttributes && !hasFiredJobClickTracking.current;
    if (shouldFireAutoOpenJobClick) {
      hasFiredJobClickTracking.current = true;
      triggerJobClickTracking(jobClickTrackingAttributes);
    }

    // Apply-start rawclick fires for IA on web and native. Native uses the
    // authenticated ViewJob bridge; the auto-open job-card rawclick remains web-only.
    const rawClickAttrs = {
      tk: mobtk,
      jobKey,
    };
    const rawClickOptions = {
      surface: resolveRawClickSurface(from, rnViewJobDisplay, isBottomSheet),
      ia: true,
      isFirstAutoLoad: !!jobClickTrackingAttributes,
      dgToken: initialData?.dgToken,
      viewJobDisplay: rnViewJobDisplay,
      ctk,
      accountId,
      grps: proctorGroups,
    };

    if (shouldFireAutoOpenJobClick) {
      const autoOpenJobClickRawClickAttrs = {
        ...rawClickAttrs,
        tk: jobClickTrackingAttributes?.tk ?? mobtk,
      };
      // first auto load job card click
      triggerRawClickLogging(autoOpenJobClickRawClickAttrs, isLoggedIn, {
        ...rawClickOptions,
        isApplyStartClick: false,
      });
    }

    // apply start click
    triggerRawClickLogging(rawClickAttrs, isLoggedIn, {
      ...rawClickOptions,
      isApplyStartClick: true,
    });

    // Mark apply start for dwell timing
    markApplyStart();
    void updateApplyStartCookie(jobKey, "i", domain);

    // Log employer outreach apply if this is an employer outreach job
    if (isFromEmployerOutreach && invite && rezcowBaseUrl && resumeBaseUrl) {
      await handleEmployerOutreachApply({
        invite,
        rezcowBaseUrl,
        resumeBaseUrl,
        jobKey,
        outreachSource,
        outreachType: outreachInfo?.outreachType,
        accountKey,
        ctk,
        mobtk,
        entryPoint,
      });
    }

    // For web, this will just be an anchor tag; we don't have to manually navigate
    if (Platform.OS !== "web") {
      const result = await handleStartIndeedApply(indeedApplyUrl);
      eventEmitter.emit("finishApply", {
        jobKeys: jobKey ? [jobKey] : [],
        applyType: ApplyType.INDEED_APPLY,
        status:
          result === NativeApplyResult.SUCCESS
            ? ApplyStatus.CONFIRMED
            : result === NativeApplyResult.CANCEL
              ? ApplyStatus.CANCELLED
              : ApplyStatus.UNKNOWN,
      });
    }
    //TODO: provide fallback option if the command not exist or get error in the handleStartIndeedApply command
    onClick?.();
  }, [
    skipNativeApplyBridge,
    tmnUrl,
    jobKey,
    domain,
    indeedApplyUrl,
    onClick,
    markApplyStart,
    isFromEmployerOutreach,
    outreachSource,
    entryPoint,
    outreachInfo,
    accountKey,
    ctk,
    mobtk,
    invite,
    rezcowBaseUrl,
    resumeBaseUrl,
    handleStartIndeedApply,
    jobClickTrackingAttributes,
    isLoggedIn,
    rnViewJobDisplay,
    isBottomSheet,
    from,
    initialData?.dgToken,
    accountId,
    proctorGroups,
  ]);

  // Added ref to prevent adding a function as a dependency on the show-logging effect.
  const onFirstImpressionRef = useRef(onFirstImpression);

  // After paint: measure/log must not run in useLayoutEffect (desktop SERP TBT).
  useEffect(() => {
    if (buttonRef.current && jobKey && !isSecondaryApplyButton) {
      buttonRef.current.measure(
        (_: number, __: number, width: number, height: number, pageX: number, pageY: number) => {
          if (!hasLoggedVisibility) {
            const windowDimensions: ButtonDimensions = {
              srcWidth: screenWidth,
              srcHeight: screenHeight,
              iaButtonX: pageX,
              iaButtonY: pageY,
              iaButtonWidth: width,
              iaButtonHeight: height,
            };
            logApplyButtonShowEvent(
              jobKey,
              RNApplyButtonTypeV3.indeedApply,
              mobtk,
              windowDimensions,
            );
            setHasLoggedVisibility(true);
          }
        },
      );
    }

    if (!hasLoggedImpression.current) {
      hasLoggedImpression.current = true;
      onFirstImpressionRef?.current?.();
    }
  }, [
    jobKey,
    mobtk,
    screenHeight,
    screenWidth,
    isSecondaryApplyButton,
    hasLoggedImpression,
    hasLoggedVisibility,
  ]);

  const modelButtonDisabled = modelDisabled && !forceEnabled;
  const buttonDisabled = disabledProp || modelButtonDisabled;
  const buttonContent = modelButtonDisabled ? i18n.gettext("Applied") : buttonText || contentHtml;
  useTrackVisibility(buttonRef, {
    trackingConfig: {
      provider: "ViewJob",
      trackingKey: mobtk ?? "",
      componentName: "IndeedApplyButton",
    },
    onBecomeVisible: () => {
      if (!hasLoggedSeen) {
        setHasLoggedSeen(true);
      }
    },
    enabled: !hasLoggedSeen,
  });

  // Guard against empty/malformed indeedApplyUrls
  let isSmartApply = false;
  if (indeedApplyUrl) {
    try {
      const hostname = new URL(indeedApplyUrl).hostname;
      isSmartApply = hostname.endsWith("smartapply.indeed.com");
    } catch (error) {
      isSmartApply = false;
      logErrorOnce("IndeedApplyButton: failed to parse indeedApplyUrl", {
        jobKey,
        indeedApplyUrl,
        hasTmnUrl: !!tmnUrl,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  return (
    <Button
      testID="viewjob-indeed-apply"
      onPress={() => {
        void onClickHandler();
      }}
      variant={variant}
      ref={buttonRef}
      disabled={buttonDisabled}
      iconBefore={iconBefore}
      iconAfter={iconAfter}
      size={buttonSize}
      href={buttonDisabled ? undefined : indeedApplyUrl}
      hrefAttrs={
        isSmartApply && !isDesktopEmbedded
          ? { rel: "nofollow" }
          : { rel: "nofollow", target: "_blank" }
      }
      sx={{
        ...(isDesktop && !isBrandedWebApply && styles.desktopButton),
        ...(isDesktop && isBrandedWebApply && styles.brandedDesktopWebApply),
      }}
      interactionStyles={
        isDesktop && isBrandedWebApply ? brandedDesktopWebApplyInteractionStyles : undefined
      }
    >
      {buttonContent}
    </Button>
  );
}

const styles = StyleSheet.create({
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  brandedDesktopWebApply: {
    paddingTop: spacing.s2,
    paddingBottom: spacing.s2,
    backgroundColor: colors.primary800,
    borderWidth: 2,
    borderColor: colors.primary800,
    backgroundImage: `linear-gradient(90deg, ${colors.primary800} 0%, ${colors.primary900} 100%)`,
  } as unknown as NonNullable<React.ComponentProps<typeof Button>["sx"]>,
  desktopButton: {
    paddingTop: spacing.sc3,
    paddingBottom: spacing.sc3,
  },
});
